import { createRoot, type Root } from 'react-dom/client'
import { useTranslation } from '@/translations/i18n'
import { type TooltipStat, TooltipStats } from './tooltip-stats'

interface NeighborhoodTooltipProps {
	neighborhoodName: string
	subgeoName?: string
	stats?: TooltipStat[]
	subgeoStats?: TooltipStat[]
}

export const NeighborhoodTooltip = ({
	neighborhoodName,
	subgeoName,
	stats,
	subgeoStats
}: NeighborhoodTooltipProps) => {
	const { t } = useTranslation()

	return (
		<div className='pointer-events-none w-[300px] rounded-xl bg-neutral-white p-2 shadow-lg'>
			<div className='mb-3'>
				<h3 className='m-0 mb-1 w-full truncate font-semibold text-neutral-900 text-sm'>
					{neighborhoodName}
				</h3>
				{subgeoName && (
					<p className='m-0 w-full truncate text-neutral-600 text-xs'>
						{t('subgeo:ngbhds_part_of_subgeo', {
							name: subgeoName
						})}
					</p>
				)}
			</div>
			<div className='flex w-full flex-col gap-1'>
				<div className='grid w-full grid-cols-[1fr_0.5fr_0.5fr] items-center justify-between gap-4 font-semibold text-neutral-700 text-xs [&>h6]:text-end'>
					<h5>{t('subgeo:stat_l12m')}</h5>
					<h6>{t('subgeo:neighborhoods_short')}</h6>
					<h6>{t('subgeo:subgeos')}</h6>
				</div>
				<TooltipStats ngbhdsstats={stats} subgeoStats={subgeoStats} />
			</div>
		</div>
	)
}

const tooltipRoots = new Map<HTMLDivElement, Root>()
const rootPool: Array<{ container: HTMLDivElement; root: Root }> = []
const MAX_POOL_SIZE = 50
let cleanupScheduled = false
const pendingCleanupRoots: Root[] = []

export const createNeighborhoodTooltipElement = (
	props: NeighborhoodTooltipProps
): HTMLDivElement => {
	let container: HTMLDivElement
	let root: Root

	if (rootPool.length > 0) {
		const pooled = rootPool.pop() as {
			container: HTMLDivElement
			root: Root
		}
		container = pooled.container
		root = pooled.root
	} else {
		container = document.createElement('div')
		root = createRoot(container)
	}

	tooltipRoots.set(container, root)

	root.render(<NeighborhoodTooltip {...props} />)

	return container
}

export const cleanupTooltipRoots = () => {
	const currentRoots = Array.from(tooltipRoots.entries())
	tooltipRoots.clear()

	for (const [container, root] of currentRoots) {
		if (rootPool.length < MAX_POOL_SIZE) {
			rootPool.push({ container, root })
		} else {
			pendingCleanupRoots.push(root)
		}
	}

	if (pendingCleanupRoots.length > 0 && !cleanupScheduled) {
		cleanupScheduled = true
		requestIdleCallback(
			() => {
				const rootsToUnmount = pendingCleanupRoots.splice(0)
				for (const root of rootsToUnmount) {
					try {
						root.unmount()
					} catch (e) {
						console.warn('Failed to unmount tooltip root:', e)
					}
				}
				cleanupScheduled = false
			},
			{ timeout: 2000 }
		)
	}
}

export const destroyAllTooltips = () => {
	const allCurrentRoots = Array.from(tooltipRoots.values())
	const allPooledRoots = rootPool.map(({ root }) => root)
	const allPendingRoots = [...pendingCleanupRoots]

	tooltipRoots.clear()
	rootPool.length = 0
	pendingCleanupRoots.length = 0

	requestIdleCallback(() => {
		for (const root of allCurrentRoots) {
			try {
				root.unmount()
			} catch (e) {
				console.warn('Failed to unmount tooltip root:', e)
			}
		}

		for (const root of allPooledRoots) {
			try {
				root.unmount()
			} catch (e) {
				console.warn('Failed to unmount pooled root:', e)
			}
		}

		for (const root of allPendingRoots) {
			try {
				root.unmount()
			} catch (e) {
				console.warn('Failed to unmount pending root:', e)
			}
		}
	})
}
